import type {
  ElationWriteRequestPayload,
  ElationWriteResultPayload,
  ExtensionMessage,
  IframeMessage,
  IframeMessageType,
  PrintDocumentPayload,
} from "./types";
import { isExtensionMessage } from "./types";
import type { WriteBackRequest, WriteBackResult } from "@ext/elation/elationWriteBack";

type MessageHandler = (msg: ExtensionMessage) => void;

let listeners: MessageHandler[] = [];
let listening = false;

/**
 * The extension parent's origin (`chrome-extension://<id>`), captured from the
 * first validated inbound extension message. Used to (a) reject spoofed inbound
 * messages from a page and (b) target the PHI-bearing WRITE_ELATION post at the
 * real extension instead of broadcasting to "*". Null until the handshake.
 */
let extensionOrigin: string | null = null;

/** Test-only: reset captured origin between tests. */
export function __resetExtensionOrigin() {
  extensionOrigin = null;
}

function onMessage(event: MessageEvent) {
  if (!isExtensionMessage(event.data)) return;
  // Origin validation — PHI now flows over this channel (WRITE_ELATION carries
  // note text + patientId; ELATION_WRITE_RESULT confirms a write). Only accept
  // messages from a chrome-extension origin, and once we've seen our extension
  // parent, only from that exact origin — so a page can't spoof a result.
  if (!event.origin.startsWith("chrome-extension://")) return;
  if (extensionOrigin === null) {
    extensionOrigin = event.origin;
  } else if (event.origin !== extensionOrigin) {
    return;
  }
  for (const handler of listeners) {
    handler(event.data);
  }
}

export function addBridgeListener(handler: MessageHandler): () => void {
  listeners.push(handler);
  if (!listening) {
    window.addEventListener("message", onMessage);
    listening = true;
  }
  return () => {
    listeners = listeners.filter((h) => h !== handler);
    if (listeners.length === 0) {
      window.removeEventListener("message", onMessage);
      listening = false;
    }
  };
}

export function sendToExtension(type: IframeMessageType, payload: unknown = null) {
  const msg: IframeMessage = { source: "astrana-standalone", type, payload };
  window.parent.postMessage(msg, "*");
}

/** Call once on app init when embedded */
export function signalReady() {
  sendToExtension("IFRAME_READY");
}

/**
 * Push the current Elation scrape recipe to the sidepanel. Called right
 * after `signalReady()` on iframe mount so the sidepanel has a recipe
 * before it polls any Elation tabs. Payload is the `ScrapeRecipe` object
 * defined in `lib/elationScrapeRecipe.ts`.
 */
export function sendElationScrapeRecipe(recipe: unknown): void {
  sendToExtension("ELATION_SCRAPE_RECIPE", recipe);
}

/**
 * Ask the sidepanel to re-scrape Elation and re-send the current data. The
 * sidepanel replies with its current cached payload immediately (even if
 * unchanged), so a freshly-mounted iframe doesn't have to wait for the next
 * background poll to learn about an existing Elation session.
 */
export function requestElationRefresh(): void {
  sendToExtension("REQUEST_ELATION_REFRESH");
}

/**
 * Ask the extension shell to open `html` in a real browser tab for printing /
 * Save-as-PDF. Used instead of the in-page hidden-iframe print when embedded,
 * because `window.print()` doesn't work inside the side-panel iframe.
 *
 * The HTML carries PHI, so this posts to the CAPTURED extension origin — never
 * "*". Returns false (caller should surface an error) if the origin hasn't been
 * captured yet from a prior inbound handshake.
 */
export function requestPrintDocument(html: string, title?: string): boolean {
  if (extensionOrigin === null) return false;
  const msg: IframeMessage = {
    source: "astrana-standalone",
    type: "REQUEST_PRINT_DOCUMENT",
    payload: { html, title } satisfies PrintDocumentPayload,
  };
  window.parent.postMessage(msg, extensionOrigin);
  return true;
}

let writeSeq = 0;

/**
 * Send a write-back request to the extension and await its result. Correlates
 * the async ELATION_WRITE_RESULT reply by a per-call requestId. Resolves (never
 * rejects) with a typed error result on timeout so callers have one code path.
 */
export function requestElationWrite(
  req: WriteBackRequest,
  timeoutMs = 15_000,
): Promise<WriteBackResult> {
  const requestId = `w${++writeSeq}`;
  return new Promise((resolve) => {
    // Never broadcast note text (PHI) to "*". If we haven't captured the
    // extension's origin from a prior inbound handshake, refuse rather than
    // post to a wildcard target.
    if (extensionOrigin === null) {
      resolve({
        ok: false,
        status: 0,
        readAfterWrite: false,
        errorCode: "INJECTION_BLOCKED",
        detail: "extension origin unknown",
      });
      return;
    }
    let done = false;
    const finish = (result: WriteBackResult) => {
      if (done) return;
      done = true;
      off();
      clearTimeout(timer);
      resolve(result);
    };
    const off = addBridgeListener((msg) => {
      if (msg.type !== "ELATION_WRITE_RESULT") return;
      const p = msg.payload as ElationWriteResultPayload;
      if (p.requestId === requestId) finish(p.result);
    });
    const timer = setTimeout(
      () =>
        finish({
          ok: false,
          status: 0,
          readAfterWrite: false,
          errorCode: "INJECTION_BLOCKED",
          detail: "write timed out",
        }),
      timeoutMs,
    );
    // Targeted post to the captured extension origin — NOT "*" — since the
    // payload carries PHI.
    const msg: IframeMessage = {
      source: "astrana-standalone",
      type: "WRITE_ELATION",
      payload: { requestId, req } satisfies ElationWriteRequestPayload,
    };
    window.parent.postMessage(msg, extensionOrigin);
  });
}
